if (!customElements.get('tan-spotlight-media')) {
  class TanSpotlightMedia extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;

      this.track = this.querySelector('[data-media-track]');
      this.slides = Array.from(this.querySelectorAll('[data-media-slide]'));
      this.previousButton = this.querySelector('[data-media-previous]');
      this.nextButton = this.querySelector('[data-media-next]');
      this.dots = Array.from(this.querySelectorAll('[data-media-dot]'));
      this.status = this.querySelector('[data-media-status]');
      this.activeIndex = 0;
      this.scrollFrame = null;

      if (!this.track || !this.slides.length) return;

      this.onScroll = this.handleScroll.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onResize = this.handleResize.bind(this);

      this.previousButton?.addEventListener('click', () => this.goTo(this.activeIndex - 1));
      this.nextButton?.addEventListener('click', () => this.goTo(this.activeIndex + 1));
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('keydown', this.onKeydown);
      window.addEventListener('resize', this.onResize, { passive: true });

      this.querySelectorAll('[data-video-play]').forEach((button) => {
        button.addEventListener('click', (event) => this.playVideo(event, button));
      });

      this.querySelectorAll('video').forEach((video) => {
        video.addEventListener('ended', () => this.resetVideo(video));
      });

      this.dataset.ready = 'true';
      this.setActive(0);
    }

    disconnectedCallback() {
      if (!this.track) return;
      this.track.removeEventListener('scroll', this.onScroll);
      this.track.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('resize', this.onResize);
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.pauseAll();
    }

    handleScroll() {
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => {
        const width = this.track.clientWidth || 1;
        const nextIndex = Math.max(0, Math.min(this.slides.length - 1, Math.round(this.track.scrollLeft / width)));
        if (nextIndex !== this.activeIndex) this.setActive(nextIndex);
      });
    }

    handleKeydown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.goTo(this.activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
    }

    handleResize() {
      if (!this.slides[this.activeIndex]) return;
      this.track.scrollTo({ left: this.slides[this.activeIndex].offsetLeft, behavior: 'auto' });
    }

    goTo(index) {
      const nextIndex = Math.max(0, Math.min(this.slides.length - 1, index));
      const slide = this.slides[nextIndex];
      if (!slide) return;

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.setActive(nextIndex);
      this.track.scrollTo({ left: slide.offsetLeft, behavior: reducedMotion ? 'auto' : 'smooth' });
    }

    setActive(index) {
      this.activeIndex = index;

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === index;
        slide.classList.toggle('is-active', isActive);
        if (isActive) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');

        slide.querySelectorAll('a, [data-video-play]').forEach((control) => {
          if (isActive) control.removeAttribute('tabindex');
          else control.setAttribute('tabindex', '-1');
        });

        if (!isActive) this.pauseSlide(slide);
      });

      this.dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === index));
      if (this.status) this.status.textContent = `${index + 1} / ${this.slides.length}`;
      if (this.previousButton) this.previousButton.disabled = index === 0;
      if (this.nextButton) this.nextButton.disabled = index === this.slides.length - 1;
    }

    async playVideo(event, button) {
      event.preventDefault();
      event.stopPropagation();

      const shell = button.closest('[data-video-shell]');
      const video = shell?.querySelector('video');
      if (!shell || !video) return;

      document.querySelectorAll('tan-spotlight-media').forEach((carousel) => {
        if (carousel !== this && typeof carousel.pauseAll === 'function') carousel.pauseAll();
      });
      this.querySelectorAll('video').forEach((otherVideo) => {
        if (otherVideo !== video) this.resetVideo(otherVideo);
      });

      shell.classList.add('is-playing');
      this.classList.add('is-video-playing');
      video.controls = true;
      video.preload = 'metadata';

      try {
        await video.play();
      } catch {
        shell.classList.remove('is-playing');
        this.classList.remove('is-video-playing');
        video.controls = false;
      }
    }

    pauseSlide(slide) {
      slide.querySelectorAll('video').forEach((video) => this.resetVideo(video));
    }

    pauseAll() {
      this.querySelectorAll('video').forEach((video) => this.resetVideo(video));
    }

    resetVideo(video) {
      video.pause();
      try {
        video.currentTime = 0;
      } catch {
        // Some browsers do not expose currentTime until video metadata is available.
      }
      video.controls = false;
      video.closest('[data-video-shell]')?.classList.remove('is-playing');
      if (!this.querySelector('[data-video-shell].is-playing')) this.classList.remove('is-video-playing');
    }
  }

  customElements.define('tan-spotlight-media', TanSpotlightMedia);
}
